import React from 'react';
import { Globe2, Shield, BookOpen, Lightbulb, Handshake, Linkedin, Award } from 'lucide-react';
import Reveal from './motion/Reveal';

const About = () => {
  const markets = [
    { country: 'Australia', flag: '🇦🇺' },
    { country: 'New Zealand', flag: '🇳🇿' },
    { country: 'United States', flag: '🇺🇸' },
    { country: 'Canada', flag: '🇨🇦' },
    { country: 'United Kingdom', flag: '🇬🇧' },
    { country: 'Ireland', flag: '🇮🇪' }
  ];

  const educationPillars = [
    {
      icon: BookOpen,
      title: 'Understanding Coverage',
      description: 'Learn what is and isn\'t covered so there are no surprises when you need to claim'
    },
    {
      icon: Shield,
      title: 'Right Protection',
      description: 'Discover how to choose coverage that actually suits your needs and circumstances'
    },
    {
      icon: Lightbulb,
      title: 'Smart Decisions',
      description: 'Make informed choices with general guidance on when to seek advice from your broker or insurer'
    }
  ];

  return (
    <section id="about" data-testid="about-section" className="py-28 bg-paper">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Company intro */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-28">
          <Reveal>
            <div className="space-y-6">
              <p className="eyebrow text-cyan-700">About Symbionic</p>
              <h2 className="text-4xl lg:text-5xl font-bold text-ink leading-tight">
                Making insurance understandable for everyone
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                We're on a mission to help everyday people around the world gain a better understanding of insurance and how it works. 
                Insurance isn't a necessary evil or an industry to fear — it's a partnership built on shared trust.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                By combining smart technology with clear education, we're helping people understand their coverage, 
                make informed decisions, and ensure they have the right protection for their needs.
              </p>
              <div className="flex items-start gap-3 bg-white border rule-light rounded-2xl p-5 shadow-sm">
                <Handshake className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                <p className="text-slate-700 leading-relaxed">
                  <span className="font-semibold">Our approach:</span> We provide general guidance to help you understand insurance better, 
                  while always pointing you to consult with your broker or insurer for specific advice tailored to your situation.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative max-w-md mx-auto lg:ml-auto">
              {/* Spotlight */}
              <div className="absolute -inset-10 bg-[radial-gradient(circle_at_50%_25%,rgba(34,211,238,0.25),transparent_70%)] blur-2xl pointer-events-none"></div>
              {/* Clipped frame */}
              <div className="relative rounded-[2rem] border rule-light bg-white p-2.5 shadow-2xl shadow-slate-900/10">
                <div className="rounded-[1.6rem] overflow-hidden">
                  <img
                    src="/bruce-visser.jpg"
                    alt="Bruce Visser - Founder of Symbionic"
                    className="w-full h-auto block object-cover"
                  />
                </div>
              </div>
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-ink text-white rounded-full px-6 py-2.5 shadow-xl whitespace-nowrap">
                <span className="eyebrow text-cyan-400">Bruce Visser</span>
                <span className="text-slate-400 text-xs ml-2">Founder</span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Founder bio - Full version */}
        <Reveal>
          <div className="mb-28 bg-white rounded-3xl p-10 lg:p-14 border rule-light shadow-sm">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <Award className="w-8 h-8 text-cyan-600" />
                <h3 className="text-3xl font-bold text-ink">Founded on Real Insurance Industry Expertise</h3>
              </div>
              
              <div className="grid md:grid-cols-3 gap-10">
                <div className="md:col-span-2 space-y-4">
                  <h4 className="text-2xl font-bold text-ink">Bruce Visser, Founder</h4>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    Symbionic was founded with over <span className="font-semibold text-ink">25 years of hands-on experience</span> in the insurance industry across both New Zealand and Australia.
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    This deep industry knowledge spans insurance sales and customer service, claims processing, assessing, dispute resolution, 
                    loss adjusting, building consultancy, and technology delivery — from frontline roles to National Manager level 
                    within multiple organizations.
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    Having led large transformational change initiatives within the industry, Bruce understands the challenges from every angle — 
                    policyholders, insurers, and everyone in between. That real-world perspective shapes everything Symbionic builds.
                  </p>
                  
                  <div className="pt-6 border-t rule-light flex items-center justify-between flex-wrap gap-4">
                    <p className="text-slate-700">
                      <span className="font-semibold">Connect with Bruce on LinkedIn</span>
                    </p>
                    <a 
                      data-testid="founder-linkedin-link"
                      href="https://www.linkedin.com/posts/bruce-visser-insurance_activity-6823140488577736704--ui1?utm_source=share&utm_medium=member_android&rcm=ACoAAAepe4QBPfbQ_aPI1gM5FV2W26a6tWpbZXU"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-2 rounded-full bg-[#0077B5] hover:bg-[#006399] px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:scale-105"
                    >
                      <Linkedin className="w-4 h-4" />
                      View Profile
                    </a>
                  </div>
                </div>
                
                <div className="flex flex-col gap-4">
                  <div className="bg-paper rounded-2xl p-6 border-l-4 border-cyan-500">
                    <div className="text-4xl font-bold text-ink mb-1">25<span className="text-cyan-600">+</span></div>
                    <div className="text-slate-600 font-medium text-sm">Years in Insurance</div>
                  </div>
                  <div className="bg-paper rounded-2xl p-6 border-l-4 border-emerald-500">
                    <div className="text-4xl font-bold text-ink mb-1">2</div>
                    <div className="text-slate-600 font-medium text-sm">Countries (NZ &amp; AU)</div>
                  </div>
                  <div className="bg-paper rounded-2xl p-6 border-l-4 border-teal-500">
                    <div className="text-lg font-bold text-ink mb-1">National</div>
                    <div className="text-slate-600 font-medium text-sm">Manager Level</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Target markets */}
        <div className="mb-28">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Globe2 className="w-7 h-7 text-cyan-600" />
                <h2 className="text-4xl font-bold text-ink">Global reach, local focus</h2>
              </div>
              <p className="text-lg text-slate-600">
                Initially targeting English-speaking markets with similar insurance terms and conditions
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {markets.map((market, index) => (
              <Reveal key={index} delay={index * 0.06}>
                <div 
                  className="bg-white rounded-2xl p-6 text-center border rule-light hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group cursor-pointer"
                >
                  <div className="text-5xl mb-3 group-hover:scale-110 transition-transform duration-300">
                    {market.flag}
                  </div>
                  <div className="text-sm font-semibold text-slate-700">{market.country}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Insurance education story */}
        <Reveal>
          <div className="bg-ink rounded-3xl p-10 lg:p-16 text-white relative overflow-hidden mb-24">
            <div className="absolute -top-32 -left-32 w-96 h-96 bg-cyan-500/15 rounded-full blur-[110px]"></div>
            <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-[110px]"></div>
            
            <div className="relative z-10">
              <div className="max-w-4xl mx-auto space-y-10">
                <div className="text-center space-y-5">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded-2xl flex items-center justify-center mx-auto">
                    <Handshake className="w-8 h-8 text-ink" />
                  </div>
                  <h2 className="text-4xl font-bold">Insurance is really just sharing the risk</h2>
                </div>

                <div className="space-y-6 text-lg text-slate-400 leading-relaxed">
                  <p>
                    The concept of insurance traces back centuries to traders taking their wares to market in boats. 
                    They discovered something brilliant: if they distributed their stock between multiple boats, 
                    then even if one capsized, everyone still had something to sell at market.
                  </p>
                  <p>
                    <span className="text-white font-semibold">It really is as simple as that.</span> Insurance is about 
                    sharing the risk of some of the worst things that might happen to your home, car, contents, business, or even your life.
                  </p>
                  <p>
                    When everyone plays their part and acts fairly and honestly, it's actually an awesome concept. 
                    It's not about fear or distrust — it's about <span className="text-cyan-300 font-semibold">partnership and mutual support</span>.
                  </p>
                </div>

                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                    <Handshake className="w-7 h-7 text-cyan-400" />
                    <span>Our commitment to you</span>
                  </h3>
                  <div className="space-y-4 text-slate-400 leading-relaxed text-lg">
                    <p>
                      We are committed to helping make insurance simple and easy to navigate.
                    </p>
                    <p>
                      <span className="text-white font-semibold">We are not here to sell you insurance or manage your claim.</span> There are already enough companies out there doing that.
                    </p>
                    <p>
                      Our aim is simple, but our mission is complex.
                    </p>
                    <p>
                      By integrating decades of insurance experience into smart tools that anyone can use, we put the control back in your hands and make insurance simple and reliable — because that is what it should be.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Education pillars */}
        <div>
          <Reveal>
            <div className="text-center mb-14 space-y-4">
              <h2 className="text-4xl font-bold text-ink">How we help you understand insurance</h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Knowledge is power. We're here to demystify insurance and help you make confident decisions.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {educationPillars.map((pillar, index) => (
              <Reveal key={index} delay={index * 0.1}>
                <div 
                  className="h-full bg-white rounded-3xl p-8 border rule-light hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-ink rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <pillar.icon className="w-7 h-7 text-cyan-400" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-ink">{pillar.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{pillar.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
